import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { MDText } from 'i18n-react';
import SimpleModal from '@adsk/bim360-matrix-react-simple-modal';
import SearchBox from '@adsk/bim360-matrix-react-search-box';
import { useLocale } from '@adsk/bim360-matrix-react-helpers/es/locale';
import Button from '@adsk/bim360-matrix-react-button';
import EmptyState from '@adsk/bim360-matrix-react-empty-state';
import Loader from '@adsk/bim360-matrix-react-loader';
import AnimateHeight from '@adsk/bim360-matrix-react-animate-height';
import objectValues from '@adsk/bim360-matrix-react-helpers/es/objectValues';

import { ENTITY_TYPES } from './helpers/consts';
import { entityShape, pluginShape } from './helpers/propTypes';
import EntityPickerEntitiesList from './EntityPickerEntitiesList';
import EntityPickerModalFooter from './EntityPickerModalFooter';

import './_EntityPickerModal.scss';

const EntityPickerModal = ({
  container,
  show,
  onExited,
  searchExpression,
  onSearchChange,
  targetEntityType,
  onLoadMore,
  manuallySelectedIds,
  linkedEntityIds,
  entities,
  isLoading,
  error,
  onTryAgain,
  clearSelectedEntities,
  onChange,
  onCancel,
  onSave,
  onCreateSuccess,
  isSaving,
  canCreate,
  plugin,
  title,
  createFormProps
}) => {
  const { T } = useLocale(({ locale }) => new MDText(require(`./i18n/${locale}.json`)));
  const [showCreateNewForm, setShowCreateNewForm] = useState(false);
  const selectedEntitiesCount = manuallySelectedIds.length;
  const showLoader = targetEntityType && !entities.length && isLoading;
  const showError = targetEntityType && !entities.length && error && !showLoader;
  const showNotFound = targetEntityType && !entities.length && !showLoader && !showError;
  const showContent = targetEntityType && !!entities.length;

  const typePlural = T.translate(`${targetEntityType}_PLURAL`);
  const searchPlaceholder = T.translate('SEARCH_PLACEHOLDER', { type: typePlural });
  const modalTitle = title || T.translate('ENTITY_PICKER_MODAL_TITLE', { type: typePlural });
  const CreateNewEntityForm = plugin ? plugin.CreateNewEntityForm : undefined;
  const CustomPickerSearchBox = plugin ? plugin.CustomPickerSearchBox : undefined;
  const renderCreateForm = canCreate && CreateNewEntityForm;
  const onModalClose = () => {
    onExited && onExited();
    showCreateNewForm && setShowCreateNewForm(false);
  };

  return (
    <SimpleModal
      title={modalTitle}
      show={show}
      onExited={onModalClose}
      className="EntityPickerModal"
      container={container}
      disableModalExit={isSaving}
      footer={
        showCreateNewForm ? null : (
          <EntityPickerModalFooter
            selectedEntitiesCount={selectedEntitiesCount}
            clearSelectedEntities={clearSelectedEntities}
            onCancel={onCancel}
            onSave={onSave}
            isSaving={isSaving}
          />
        )
      }
    >
      {renderCreateForm && (
        <AnimateHeight
          className="EntityPickerModal__create-form-animation"
          height="0px"
          timeout={300}
          isOpen={showCreateNewForm}
        >
          <CreateNewEntityForm
            {...createFormProps}
            onClose={() => setShowCreateNewForm(false)}
            onCreateSuccess={onCreateSuccess}
            isOpen={showCreateNewForm}
            container={container}
          />
        </AnimateHeight>
      )}
      <div className="EntityPickerModal__search-bar">
        {CustomPickerSearchBox ? (
          <CustomPickerSearchBox onSearchChange={onSearchChange} isSaving={isSaving} />
        ) : (
          <SearchBox
            placeholder={searchPlaceholder}
            searchExpression={searchExpression}
            onChange={onSearchChange}
            onClear={() => onSearchChange('')}
            onSearch={() => {}}
            className="EntityPickerModal__search-box"
            isFocused
            inputDisabled={isSaving}
            searchDisabled={isSaving}
            clearDisabled={isSaving}
          />
        )}
        {canCreate && (
          <Button
            onClick={() => setShowCreateNewForm(true)}
            className="EntityPickerModal__create-button"
          >
            {T.translate('CREATE_NEW')}
          </Button>
        )}
      </div>
      {showLoader && <Loader />}
      {showError && (
        <EmptyState
          icon={EmptyState.Icons.CONE}
          className="EntityPickerModal__error"
          text={T.translate('ERROR_LOADING_METADATA')}
          footer={
            <Button styleType={Button.StyleType.LINK} onClick={onTryAgain}>
              {T.translate('TRY_AGAIN')}
            </Button>
          }
        />
      )}
      {showContent && (
        <div className="EntityPickerModal__list-container">
          <EntityPickerEntitiesList
            plugin={plugin}
            onChange={onChange}
            entityType={targetEntityType}
            isLoading={isLoading}
            onLoadMore={onLoadMore}
            error={error}
            onTryAgain={onTryAgain}
            manuallySelectedIds={manuallySelectedIds}
            linkedEntityIds={linkedEntityIds}
            entities={entities}
            disabled={isSaving}
          />
        </div>
      )}
      {showNotFound && (
        <EmptyState
          icon={EmptyState.Icons.NOT_FOUND}
          text={T.translate('NOT_FOUND', { type: typePlural })}
          className="EntityPickerModal__empty-state"
        />
      )}
    </SimpleModal>
  );
};

EntityPickerModal.propTypes = {
  /**
   * Controls whether or not to show the modal.
   */
  show: PropTypes.bool,
  /**
   * Callback to when closing the modal.
   */
  onExited: PropTypes.func.isRequired,
  /**
   * A container to attach the modal to
   */
  container: PropTypes.object,
  /**
   * Current search expression
   */
  searchExpression: PropTypes.string,
  /**
   * Callback when search expression is changed
   */
  onSearchChange: PropTypes.func.isRequired,
  /**
   * Whether we're loading something from BE.
   */
  isLoading: PropTypes.bool,
  /**
   * Whether had error fetching data from BE.
   */
  error: PropTypes.bool,
  /**
   * Callback to fetch data again.
   */
  onTryAgain: PropTypes.func,
  /**
   * The target entity type we want to allow linking to.
   */
  targetEntityType: PropTypes.oneOf(objectValues(ENTITY_TYPES)),
  /**
   * Load the next page of entities.
   */
  onLoadMore: PropTypes.func,
  /**
   * IDs of manually selected entities.
   */
  manuallySelectedIds: PropTypes.arrayOf(PropTypes.string),
  /**
   * IDs of entities already linked to current source entity.
   */
  linkedEntityIds: PropTypes.arrayOf(PropTypes.string),
  /**
   * List of entities.
   */
  entities: PropTypes.arrayOf(entityShape).isRequired,
  /**
   * Callback to clear all selected entities
   */
  clearSelectedEntities: PropTypes.func.isRequired,
  /**
   * Callback to handle selection change.
   * Handler is of shape: ({entity, entityType, checked}) => *
   */
  onChange: PropTypes.func.isRequired,
  /**
   * Save button click handler.
   */
  onSave: PropTypes.func.isRequired,
  /**
   * Handler after successful creation of a new entity.
   */
  onCreateSuccess: PropTypes.func.isRequired,
  /**
   * Whether we're currently in saving process.
   */
  isSaving: PropTypes.bool,
  /**
   * Whether the user can create entity of selected type.
   */
  canCreate: PropTypes.bool,
  /**
   * The plugin to use for selected entity type.
   */
  plugin: pluginShape,
  /**
   * Cancel button click handler.
   */
  onCancel: PropTypes.func.isRequired,
  /**
   * Modal title
   */
  title: PropTypes.string,
  /**
   * Additional props to pass to create form.
   */
  createFormProps: PropTypes.object
};

export default EntityPickerModal;
